import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { usersApi } from '@/api/users'
import { updateUserSchema, UpdateUserData } from '@/schemas/user'
import type { User as UserType } from '@/api/users'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	AtSign,
	Contact,
	CreditCard,
	Eye,
	EyeOff,
	IdCard,
	Loader2,
	Lock,
	MapPin,
	MessageCircle,
	MessageSquare,
	Phone,
	User,
	UserRound,
} from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DepartmentSelectField } from '@/components/departments/DepartmentSelectField'
import { ImageUploadField } from '@/components/ui/image-upload'
import { showError } from '@/lib/error'
import { useUser } from '@/stores/app'

interface EditUserFormProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	user: UserType | null
	onSuccess?: () => void
}

export function EditUserForm({ open, onOpenChange, user, onSuccess }: EditUserFormProps) {
	const [showPassword, setShowPassword] = useState(false)
	const queryClient = useQueryClient()
	const { user: currentUser } = useUser()

	const updateMutation = useMutation({
		mutationFn: (data: UpdateUserData & { id: number }) => {
			const { id, ...rest } = data
			return usersApi.update(id, rest)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
	})

	const superAdminMutation = useMutation({
		mutationFn: ({ id, is_super_admin }: { id: number; is_super_admin: boolean }) =>
			usersApi.updateSuperAdmin(id, is_super_admin),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] })
		},
	})

	const form = useForm<UpdateUserData>({
		resolver: zodResolver(updateUserSchema),
		values: user ? {
			username: user.username || '',
			nickname: user.nickname || '',
			email: user.email || '',
			phone: user.phone || '',
			avatar: user.avatar || '',
			department_id: user.department_id ?? null,
			status: user.status,
			is_super_admin: user.is_super_admin ?? false,
			password: '',
			wechat: user.wechat || '',
			gender: user.gender ?? 0,
			card_no: user.card_no || '',
			card_front: user.card_front || '',
			card_back: user.card_back || '',
			emergency_contact: user.emergency_contact || '',
			emergency_mobile: user.emergency_mobile || '',
			address: user.address || '',
			remark: user.remark || '',
			self_discipline: user.self_discipline || '',
		} : undefined,
	})

	const onSubmit = async (data: UpdateUserData) => {
		if (!user) return
		try {
			const submitData = { ...data }
			// 密码留空不发送
			if (!submitData.password) {
				delete submitData.password
			}
			const isSuperAdminChanged = submitData.is_super_admin !== user.is_super_admin
			delete submitData.is_super_admin

			await updateMutation.mutateAsync({ id: user.id, ...submitData })

			if (isSuperAdminChanged) {
				await superAdminMutation.mutateAsync({ id: user.id, is_super_admin: data.is_super_admin ?? false })
			}

			toast.success('用户更新成功')
			onOpenChange(false)
			onSuccess?.()
		} catch (error) {
			console.error('更新用户失败:', error)
			showError(error, '更新用户失败')
		}
	}

	const handleClose = () => {
		onOpenChange(false)
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
				<DialogHeader className="shrink-0 border-b px-6 py-5">
					<DialogTitle>编辑用户</DialogTitle>
					<DialogDescription>
						修改用户 {user?.nickname || user?.username} 的信息
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
						<div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
							<div className="space-y-4">
								<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
									<UserRound className="h-4 w-4" />
									基本信息
									</h4>
									<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									<FormField control={form.control} name="username" render={({ field }) => (
										<FormItem>
											<FormLabel>用户名 <span className="text-destructive">*</span></FormLabel>
											<FormControl>
												<div className="relative">
													<User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="请输入用户名" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)} />
									<FormField control={form.control} name="nickname" render={({ field }) => (
										<FormItem>
										<FormLabel>昵称</FormLabel>
										<FormControl>
											<div className="relative">
												<Contact className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="请输入昵称" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />
								<FormField control={form.control} name="email" render={({ field }) => (
									<FormItem>
										<FormLabel>邮箱</FormLabel>
										<FormControl>
											<div className="relative">
												<AtSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="example@email.com" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />
								<FormField control={form.control} name="phone" render={({ field }) => (
									<FormItem>
										<FormLabel>手机号 <span className="text-destructive">*</span></FormLabel>
										<FormControl>
											<div className="relative">
												<Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="13800138000" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)} />
								<Controller control={form.control} name="department_id" render={({ field }) => (
									<FormItem>
										<FormLabel>部门</FormLabel>
										<FormControl><DepartmentSelectField value={field.value ?? null} onChange={field.onChange} /></FormControl>
										<FormMessage />
									</FormItem>
								)} />
								<FormField control={form.control} name="status" render={({ field }) => (
									<FormItem>
										<FormLabel>状态</FormLabel>
										<Select value={String(field.value ?? 1)} onValueChange={(val) => field.onChange(Number(val))}>
											<FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
											<SelectContent>
												<SelectItem value="1">正常</SelectItem>
												<SelectItem value="0">禁用</SelectItem>
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)} />
								<FormField control={form.control} name="is_super_admin" render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
										<div className="space-y-0.5">
											<FormLabel>是否超管</FormLabel>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
												disabled={String(currentUser?.id) === String(user?.id)}
											/>
										</FormControl>
									</FormItem>
								)} />
								</div>
							</div>

						<Separator />

						{/* 个人信息（同步自 oa-backend） */}
						<div className="space-y-4">
							<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
								<IdCard className="h-4 w-4" />
								个人信息
							</h4>

							<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
								<FormField
									control={form.control}
									name="wechat"
									render={({ field }) => (
										<FormItem>
											<FormLabel>微信号</FormLabel>
											<FormControl>
												<div className="relative">
													<MessageCircle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
													<Input placeholder="请输入微信号" className="pl-9" {...field} />
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="gender"
									render={({ field }) => (
										<FormItem>
											<FormLabel>性别</FormLabel>
											<Select
												value={String(field.value ?? 0)}
												onValueChange={(val) => field.onChange(Number(val))}
											>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="请选择" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													<SelectItem value="0">未知</SelectItem>
													<SelectItem value="1">男</SelectItem>
													<SelectItem value="2">女</SelectItem>
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="card_no"
								render={({ field }) => (
									<FormItem>
										<FormLabel>身份证号</FormLabel>
										<FormControl>
											<div className="relative">
												<CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="请输入身份证号" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
								<FormField
									control={form.control}
									name="card_front"
									render={({ field }) => (
										<FormItem>
											<FormLabel>身份证正面照</FormLabel>
											<FormControl>
												<ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="正面照" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="card_back"
									render={({ field }) => (
										<FormItem>
											<FormLabel>身份证背面照</FormLabel>
											<FormControl>
												<ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="背面照" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="self_discipline"
									render={({ field }) => (
										<FormItem>
											<FormLabel>业务自律书</FormLabel>
											<FormControl>
												<ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="自律书" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
								<FormField
									control={form.control}
									name="emergency_contact"
									render={({ field }) => (
										<FormItem>
										<FormLabel>紧急联系人</FormLabel>
										<FormControl>
											<div className="relative">
												<Contact className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="紧急联系人姓名" className="pl-9" {...field} />
											</div>
										</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="emergency_mobile"
									render={({ field }) => (
										<FormItem>
										<FormLabel>紧急联系人电话</FormLabel>
										<FormControl>
											<div className="relative">
												<Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="紧急联系人电话" className="pl-9" {...field} />
											</div>
										</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="address"
								render={({ field }) => (
									<FormItem>
										<FormLabel>地址</FormLabel>
										<FormControl>
											<div className="relative">
												<MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="请输入地址" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="remark"
								render={({ field }) => (
									<FormItem>
										<FormLabel>备注</FormLabel>
										<FormControl>
											<div className="relative">
												<MessageSquare className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input placeholder="备注信息" className="pl-9" {...field} />
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<Separator />

						{/* 密码设置 */}
						<div className="space-y-4">
							<h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
								<Lock className="h-4 w-4" />
								密码设置
							</h4>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>新密码</FormLabel>
										<FormControl>
											<div className="relative">
												<Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input
													type={showPassword ? "text" : "password"}
													placeholder="留空则不更改密码"
													className="px-9"
													{...field}
												/>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
													onClick={() => setShowPassword(!showPassword)}
												>
													{showPassword ? (
														<EyeOff className="h-4 w-4 text-muted-foreground" />
													) : (
														<Eye className="h-4 w-4 text-muted-foreground" />
													)}
												</Button>
											</div>
										</FormControl>
										<FormDescription>
											留空则不更改密码，6-32个字符
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						</div>

						<DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:gap-0">
							<Button type="button" variant="outline" onClick={handleClose}>
								取消
							</Button>
							<Button type="submit" disabled={updateMutation.isPending || superAdminMutation.isPending}>
								{(updateMutation.isPending || superAdminMutation.isPending) && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								保存修改
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
